// Step 4: which of the user's secrets meets each of the workflow's requirements. The obvious
// ones are chosen already (the source's own secret, the only match, the platform key); the
// agent's other needs (its own VM, an OAuth connection) are listed for information. Then the
// toolchains for the group's own worker image (D90), those the workflow requires already ticked.
import { Cube, Plugs, ShieldCheck } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { useT } from "../../../i18n";
import { IconTile } from "../../../ui/Card";
import { TemplateIcon } from "../../../secrets/TemplatePicker";
import { templateOf } from "../../../secrets/templates";
import { EmptyState } from "../../../ui/states";
import { Pill } from "../../../ui/status";
import { requirementLabel } from "../../../workflows/requirements/model";
import { ToolchainsEditor } from "../../toolchains/ToolchainsEditor";
import { accepts, type Credential, type Draft, kindFor, requiredToolchainsOf, requirementsOf } from "../draft";
import { CredentialSelect } from "../source/CredentialSelect";

function Row({ icon, title, badge, children }: { icon: ReactNode; title: string; badge?: ReactNode; children?: ReactNode }) {
  return (
    <li className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-start">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {icon}
        <div className="min-w-0 pt-1">
          <div className="text-[14px] font-semibold text-gray-900">{title}</div>
          {badge ? <div className="mt-1">{badge}</div> : null}
        </div>
      </div>
      {children ? <div className="sm:w-72 sm:shrink-0">{children}</div> : null}
    </li>
  );
}

interface Props {
  draft: Draft;
  onChange: (patch: Partial<Draft>) => void;
  credentials: Credential[];
  onAdd?: (kind: string, index: number) => void;
}

export function AccessStep({ draft, onChange, credentials, onAdd }: Props) {
  const t = useT().tasks.create.access;
  const requirements = requirementsOf(draft.workflow);
  const toolchains = (
    <section aria-label={t.toolchains} className="space-y-3 border-t border-muted pt-6">
      <div>
        <h3 className="m-0 text-[15px] font-semibold text-gray-900">{t.toolchains}</h3>
        <p className="mb-0 mt-1 text-[13px] text-gray-500">{t.toolchainsHint}</p>
      </div>
      <ToolchainsEditor presets={draft.toolchains} onPresets={(next) => onChange({ toolchains: next })} required={requiredToolchainsOf(draft.workflow)} />
    </section>
  );
  if (requirements.every((r) => r.kind === "toolchain")) {
    return (
      <div className="space-y-6">
        <EmptyState compact icon={<ShieldCheck weight="duotone" />} title={t.none}>
          {t.noneBody}
        </EmptyState>
        {toolchains}
      </div>
    );
  }
  const bind = (index: number, value: string) => onChange({ bindings: { ...draft.bindings, [index]: value } });
  return (
    <div className="space-y-6">
    <ul className="m-0 list-none divide-y divide-muted/70 p-0">
      {requirements.map((r, index) => {
        if (r.kind === "toolchain") return null;
        if (r.kind === "isolation") {
          return <Row key={index} icon={<IconTile className="bg-blue-lighter text-blue-dark"><Cube weight="duotone" /></IconTile>} title={requirementLabel(r)} badge={<span className="text-[13px] text-gray-500">{t.vm}</span>} />;
        }
        if (r.kind === "oauth") {
          return <Row key={index} icon={<IconTile className="bg-gray-100 text-gray-700"><Plugs weight="duotone" /></IconTile>} title={requirementLabel(r)} badge={<span className="text-[13px] text-gray-500">{t.oauth}</span>} />;
        }
        const matches = credentials.filter((c) => accepts(r, c.kind));
        const extra = r.platform_default ? [{ value: "platform", label: t.platformKey }] : [];
        return (
          <Row
            key={index}
            icon={<TemplateIcon id={templateOf({ kind: kindFor(r), injection: null })?.id} />}
            title={requirementLabel({ ...r, required: true })}
            badge={<Pill tone={r.required && !r.platform_default ? "orange" : "gray"}>{r.required && !r.platform_default ? t.required : t.optional}</Pill>}
          >
            <CredentialSelect
              label={t.use}
              matches={matches}
              value={draft.bindings[index] ?? ""}
              onChange={(id) => bind(index, id)}
              extra={extra}
              onAdd={onAdd && (() => onAdd(kindFor(r), index))}
              missing={t.noMatch}
            />
          </Row>
        );
      })}
    </ul>
    {toolchains}
    </div>
  );
}
